const highlights = [
  {
    icon: 'ri-search-eye-line',
    label: 'Ultra-Rare Condition',
    detail: 'Fewer than 30 cases have been genetically confirmed in medical literature worldwide as of 2025, making CSCF one of the rarest genetic conditions ever described.',
    color: 'text-brand-500',
    bg: 'bg-brand-50',
    border: 'border-brand-100',
  },
  {
    icon: 'ri-body-scan-line',
    label: 'Multi-System Impact',
    detail: 'CSCF affects the heart, spine, hands, feet, hearing, face, and development simultaneously — requiring coordinated care across multiple medical specialties.',
    color: 'text-accent-500',
    bg: 'bg-accent-50',
    border: 'border-accent-100',
  },
  {
    icon: 'ri-git-branch-line',
    label: 'Genetic Diagnosis Required',
    detail: 'Confirmation of CSCF requires whole exome sequencing (WES) to identify pathogenic variants in the MAP3K7 gene. Clinical features alone are insufficient for diagnosis.',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
  },
];

const timeline = [
  { year: '2016', event: 'MAP3K7 gene identified as cause of CSCF', detail: 'Le Goff et al. published the landmark paper defining CSCF as a distinct genetic syndrome caused by MAP3K7 variants.', icon: 'ri-flag-line' },
  { year: '2018', event: 'Connective tissue overlap described', detail: 'Morlino et al. reported CSCF presenting alongside hereditary connective tissue disorder features, expanding the phenotype.', icon: 'ri-file-paper-2-line' },
  { year: '2020', event: 'Novel variant & expanded phenotype', detail: 'AbuBakr et al. described new features including elbow contractures and wrinkled palms, broadening the clinical spectrum.', icon: 'ri-add-circle-line' },
  { year: '2023', event: 'Orofacial clefting reported', detail: 'Shepherd et al. described the first CSCF case with orofacial clefting, further expanding the known phenotype.', icon: 'ri-file-add-line' },
  { year: '2024', event: 'Severe phosphorylation site variant', detail: 'Nyuzuki et al. reported the most severe CSCF case to date, with a variant at the critical TAK1 autophosphorylation site.', icon: 'ri-alert-line' },
  { year: '2025', event: 'DCM & novel complications reported', detail: 'Two new papers described dilated cardiomyopathy and previously unreported complications including intestinal obstruction.', icon: 'ri-newspaper-line' },
];

const researchNeeds = [
  { icon: 'ri-group-2-line', title: 'Patient Registry', text: 'A global registry of confirmed CSCF cases would enable researchers to identify patterns, track outcomes, and design studies.' },
  { icon: 'ri-test-tube-line', title: 'Natural History Studies', text: 'Long-term follow-up data is needed to understand how CSCF progresses across the lifespan and which interventions are most effective.' },
  { icon: 'ri-capsule-line', title: 'Therapeutic Research', text: 'No targeted therapies exist for CSCF. Research into TAK1 pathway modulation could open new treatment avenues.' },
  { icon: 'ri-microscope-line', title: 'Genotype-Phenotype Studies', text: 'Understanding why different MAP3K7 variants cause different severity levels is critical for prognosis and counseling.' },
];

export default function ResearchContent() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 space-y-20">

        {/* Main text + highlight box — 2 col */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">

          {/* LEFT: Text */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 font-heading mb-4">
                Why Research Matters
              </h2>
              <p className="text-gray-600 leading-relaxed font-body mb-4">
                Cardiospondylocarpofacial syndrome (CSCF) is caused by heterozygous pathogenic variants in the <strong className="text-gray-800">MAP3K7</strong> gene, which encodes TGF-β-activated kinase 1 (TAK1) — a protein kinase critical to embryonic development and multiple cellular signaling pathways.
              </p>
              <p className="text-gray-600 leading-relaxed font-body mb-4">
                Because CSCF is so rare, the medical community has very limited data on its natural history, long-term outcomes, and optimal management. Most clinicians will never encounter a single case in their career. This means families often receive fragmented care from specialists who are unfamiliar with the full picture of the condition.
              </p>
              <p className="text-gray-600 leading-relaxed font-body">
                Every new case report, every family who participates in research, and every clinician who takes an interest in CSCF contributes to a growing body of knowledge that will ultimately improve outcomes for all affected individuals.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 space-y-4">
              <h3 className="text-sm font-bold text-gray-900 font-heading flex items-center gap-2">
                <i className="ri-bar-chart-2-line text-brand-500"></i>
                Current State of Research
              </h3>
              {[
                { label: 'Confirmed cases worldwide', value: '< 30', color: 'text-brand-500' },
                { label: 'Published case studies', value: '7+', color: 'text-accent-500' },
                { label: 'Known MAP3K7 variants', value: '10+', color: 'text-emerald-500' },
                { label: 'Years since gene identified', value: '9', color: 'text-amber-500' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm text-gray-600 font-body">{stat.label}</span>
                  <span className={`text-lg font-bold font-heading ${stat.color}`}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Highlight box with bullet points */}
          <div className="space-y-5">
            <h2 className="text-2xl font-bold text-gray-900 font-heading mb-2">
              Key Facts for Researchers
            </h2>
            <p className="text-gray-500 text-sm font-body mb-6 leading-relaxed">
              If you are a clinician or researcher encountering CSCF for the first time, these are the most important things to understand.
            </p>

            {highlights.map((h) => (
              <div
                key={h.label}
                className={`flex items-start gap-4 p-5 rounded-2xl border ${h.bg} ${h.border}`}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl flex-shrink-0">
                  <i className={`${h.icon} text-xl ${h.color}`}></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 font-heading text-sm mb-1">{h.label}</h4>
                  <p className="text-sm text-gray-600 font-body leading-relaxed">{h.detail}</p>
                </div>
              </div>
            ))}

            {/* Link to full science page */}
            <div className="pt-2">
              <a
                href="/science"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-500 hover:text-brand-600 transition-colors cursor-pointer font-body"
              >
                <i className="ri-external-link-line"></i>
                View all published case studies and references
              </a>
            </div>
          </div>
        </div>

        {/* Featured Study — PCOM Dissertation */}
        <div id="featured-study" className="rounded-3xl border-2 border-amber-200 bg-amber-50 overflow-hidden">
          {/* Header bar */}
          <div className="flex items-center justify-between px-8 lg:px-10 py-4 bg-amber-100/70 border-b border-amber-200">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-400 rounded-full text-xs font-bold text-white font-heading tracking-wider">
                <i className="ri-star-fill"></i>
                FEATURED STUDY
              </span>
              <span className="text-xs text-amber-700 font-body font-medium">2025 · PCOM Psychology Dissertations</span>
            </div>
            <a
              href="/science#references"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 transition-colors whitespace-nowrap cursor-pointer font-body"
            >
              <i className="ri-book-open-line"></i>
              View all related studies
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] items-stretch">
            <div className="p-8 lg:p-10">
              <h3 className="text-xl font-bold text-gray-900 font-heading mb-3 leading-snug">
                Identifying Needs of School-age Children with Cardiospondylocarpofacial Syndrome
              </h3>
              <p className="text-sm text-gray-600 font-body leading-relaxed mb-5">
                This landmark descriptive case series — the first of its kind to examine the <strong className="text-gray-800">behavioral, social, emotional, and academic functioning</strong> of school-age children with CSCF — studied eight children recruited from North America, Europe, and Australia via our private Facebook community. All children experienced developmental delays and ongoing health issues. A consistent theme was the uncertainty faced by families due to the limited availability of information about CSCF.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Author', value: 'Erin Henning, PsyD' },
                  { label: 'Institution', value: 'Philadelphia College of Osteopathic Medicine' },
                  { label: 'Degree', value: 'Doctor of Psychology (PsyD)' },
                  { label: 'Downloads', value: '231+' },
                ].map((item) => (
                  <div key={item.label} className="bg-white border border-amber-100 rounded-xl p-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider font-heading mb-1">{item.label}</p>
                    <p className="text-xs text-gray-700 font-body leading-snug">{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <a
                  href="https://digitalcommons.pcom.edu/psychology_dissertations/687/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-full hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
                >
                  <i className="ri-external-link-line"></i>
                  Read Full Study
                </a>
                <a
                  href="https://digitalcommons.pcom.edu/cgi/viewcontent.cgi?article=1686&context=psychology_dissertations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-amber-200 text-amber-700 font-semibold rounded-full hover:border-amber-400 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
                >
                  <i className="ri-download-line"></i>
                  Download PDF
                </a>
                <a
                  href="/science#references"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-amber-200 text-amber-700 font-semibold rounded-full hover:border-amber-400 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
                >
                  <i className="ri-book-2-line"></i>
                  See Related Studies
                </a>
              </div>
            </div>
            <div className="hidden lg:flex flex-col items-center justify-center bg-amber-100/60 px-10 gap-3 min-w-[160px]">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-200 rounded-2xl">
                <i className="ri-graduation-cap-line text-amber-700 text-3xl"></i>
              </div>
              <p className="text-xs font-bold text-amber-700 font-heading text-center tracking-wide">PCOM<br />PSYCHOLOGY<br />DISSERTATIONS</p>
            </div>
          </div>
        </div>

        {/* Research timeline */}
        <div>
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1.5 bg-brand-50 border border-brand-100 rounded-full mb-4">
              <span className="text-xs font-semibold text-brand-600 tracking-widest font-heading">TIMELINE</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading mb-3">
              Research Milestones
            </h2>
            <p className="text-gray-500 text-sm font-body max-w-lg mx-auto">
              From the first genetic identification in 2016 to the most recent case reports in 2025.
            </p>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-brand-100 hidden md:block" />

            <div className="space-y-6">
              {timeline.map((item, i) => (
                <div key={i} className="flex items-start gap-6">
                  {/* Year bubble */}
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-brand-50 border-2 border-brand-200 rounded-full z-10">
                    <i className={`${item.icon} text-brand-500 text-sm`}></i>
                  </div>
                  {/* Content */}
                  <div className="flex-1 bg-gray-50 border border-gray-100 rounded-2xl p-5 pb-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-bold text-brand-500 font-heading bg-brand-50 border border-brand-100 px-2.5 py-0.5 rounded-full">
                        {item.year}
                      </span>
                      <h4 className="text-sm font-bold text-gray-900 font-heading">{item.event}</h4>
                    </div>
                    <p className="text-xs text-gray-500 font-body leading-relaxed">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Research needs grid */}
        <div>
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1.5 bg-accent-50 border border-accent-100 rounded-full mb-4">
              <span className="text-xs font-semibold text-accent-600 tracking-widest font-heading">WHAT IS NEEDED</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading mb-3">
              Critical Research Gaps
            </h2>
            <p className="text-gray-500 text-sm font-body max-w-lg mx-auto">
              These are the areas where new research would have the greatest impact for families living with CSCF.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {researchNeeds.map((need) => (
              <div key={need.title} className="bg-white border border-gray-100 rounded-2xl p-6 flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-accent-50 rounded-xl flex-shrink-0">
                  <i className={`${need.icon} text-accent-500 text-xl`}></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 font-heading mb-2">{need.title}</h4>
                  <p className="text-sm text-gray-500 font-body leading-relaxed">{need.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
